import { useCallback, useEffect, useState } from "react";
import { Loader2, MailPlus, Pencil, PlugZap, Server, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { adminApi } from "@/lib/auth";
import { toast } from "sonner";

const emptyForm = {
  host: "mail.desfiemer.com", port: 993, user: "", password: "", active: true,
};

const fmt = (v) =>
  v ? new Date(v).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

const StatusChip = ({ box }) => {
  if (!box.active) return <span className="text-[11px] font-semibold uppercase text-zinc-400">Desactivado</span>;
  if (box.failing_since)
    return (
      <span className="rounded-sm border border-red-300 bg-red-50 px-2 py-0.5 text-[11px] font-bold uppercase text-red-800">
        Sin conectar desde {fmt(box.failing_since)}
      </span>
    );
  if (box.last_ok)
    return (
      <span className="rounded-sm border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] font-bold uppercase text-emerald-800">
        Leído {fmt(box.last_ok)}
      </span>
    );
  return <span className="text-[11px] font-semibold uppercase text-zinc-500">Aún no leído</span>;
};

const Field = ({ label, children }) => (
  <div>
    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-zinc-600">{label}</label>
    {children}
  </div>
);

const MailboxDialog = ({ open, onOpenChange, editing, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTestResult(null);
    setForm(editing ? {
      host: editing.host, port: editing.port, user: editing.user, password: "", active: editing.active,
    } : emptyForm);
  }, [open, editing]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await adminApi.post("/admin/reception/mailboxes/test", {
        id: editing?.id || null, host: form.host.trim(), port: Number(form.port) || 993,
        user: form.user.trim(), password: form.password,
      });
      setTestResult(res.data);
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "No se pudo probar." });
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    if (!form.user.trim() || !form.host.trim()) {
      toast.error("Escribe el servidor y el usuario del buzón.");
      return;
    }
    if (!editing && !form.password) {
      toast.error("Escribe la contraseña del buzón.");
      return;
    }
    setSaving(true);
    try {
      const body = { ...form, kind: "pedidos", host: form.host.trim(), user: form.user.trim(), port: Number(form.port) || 993 };
      if (editing) await adminApi.put(`/admin/reception/mailboxes/${editing.id}`, body);
      else await adminApi.post("/admin/reception/mailboxes", body);
      toast.success(editing ? "Buzón actualizado." : "Buzón añadido.");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "No se pudo guardar el buzón.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="mailbox-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar buzón" : "Nuevo buzón de pedidos"}</DialogTitle>
          <DialogDescription>
            La app lee este buzón cada 15 minutos: los pedidos crean y actualizan cursos y la remesa de BSAFE marca
            las facturas cobradas. Los datos son los mismos que usa tu programa de correo (IMAP).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Correo (usuario)">
            <Input value={form.user} onChange={set("user")} placeholder="formacion@desfiemer.com"
              className="h-11 border-2 border-zinc-300" data-testid="mailbox-user-input" />
          </Field>
          <Field label={editing ? "Contraseña (déjala vacía para no cambiarla)" : "Contraseña"}>
            <Input type="password" value={form.password} onChange={set("password")} autoComplete="new-password"
              className="h-11 border-2 border-zinc-300" data-testid="mailbox-password-input" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Field label="Servidor IMAP">
                <Input value={form.host} onChange={set("host")} className="h-11 border-2 border-zinc-300"
                  data-testid="mailbox-host-input" />
              </Field>
            </div>
            <Field label="Puerto">
              <Input type="number" value={form.port} onChange={set("port")} className="h-11 border-2 border-zinc-300"
                data-testid="mailbox-port-input" />
            </Field>
          </div>
          <label className="flex items-center gap-3 text-sm font-semibold text-zinc-800">
            <Switch checked={form.active} onCheckedChange={set("active")} data-testid="mailbox-active-switch" />
            Leer este buzón
          </label>
          {testResult && (
            <p
              className={`rounded-sm border px-3 py-2 text-sm font-semibold ${
                testResult.ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-red-300 bg-red-50 text-red-800"
              }`}
              data-testid="mailbox-test-result"
            >
              {testResult.ok ? "Conexión correcta: la app puede leer este buzón." : `No conecta: ${testResult.error}`}
            </p>
          )}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={test} disabled={testing} className="h-10 border-2 border-zinc-300"
            data-testid="mailbox-test-btn">
            {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlugZap className="mr-2 h-4 w-4" />}
            Probar conexión
          </Button>
          <Button onClick={save} disabled={saving} className="h-10 bg-zinc-950 hover:bg-zinc-800"
            data-testid="mailbox-save-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const MailboxesPanel = ({ onChange }) => {
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await adminApi.get("/admin/reception/mailboxes");
      setBoxes(res.data?.mailboxes || []);
    } catch (e) {
      toast.error("No se pudieron cargar los buzones.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const afterChange = () => { load(); onChange?.(); };

  const remove = async () => {
    try {
      await adminApi.delete(`/admin/reception/mailboxes/${toDelete.id}`);
      toast.success("Buzón eliminado.");
      afterChange();
    } catch (e) {
      toast.error("No se pudo eliminar el buzón.");
    } finally {
      setToDelete(null);
    }
  };

  return (
    <section className="rounded-sm border-2 border-zinc-200 bg-white p-4" data-testid="mailboxes-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-extrabold text-zinc-950">
            <Server className="h-5 w-5 text-zinc-700" />
            Buzones de pedidos de cursos
          </h3>
          <p className="mt-1 text-sm text-zinc-600">
            Puedes tener varios. De cada uno se leen los pedidos de cursos y también la remesa mensual de BSAFE
            cuando llega a él.
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }}
          className="h-10 bg-zinc-950 font-semibold hover:bg-zinc-800" data-testid="mailbox-add-btn">
          <MailPlus className="mr-2 h-4 w-4" />
          Añadir buzón
        </Button>
      </div>

      {loading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-zinc-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando buzones…
        </div>
      ) : boxes.length === 0 ? (
        <div className="mt-3 rounded-sm border-2 border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900"
          data-testid="mailboxes-empty">
          No hay ningún buzón dado de alta: la app no está leyendo ningún correo. Pulsa «Añadir buzón».
        </div>
      ) : (
        <ul className="mt-3 divide-y divide-zinc-100" data-testid="mailboxes-list">
          {boxes.map((b) => (
            <li key={b.id} className="flex flex-wrap items-center justify-between gap-3 py-2.5" data-testid={`mailbox-row-${b.id}`}>
              <div className="min-w-0">
                <p className="truncate font-mono text-sm font-bold text-zinc-900">{b.user}</p>
                <p className="text-xs text-zinc-500">{b.host}:{b.port}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusChip box={b} />
                <Button size="sm" variant="ghost" onClick={() => { setEditing(b); setDialogOpen(true); }}
                  aria-label="Editar buzón" data-testid={`mailbox-edit-${b.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setToDelete(b)} className="text-red-700 hover:bg-red-50"
                  aria-label="Eliminar buzón" data-testid={`mailbox-delete-${b.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <MailboxDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} onSaved={afterChange} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="mailbox-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar el buzón {toDelete?.user}?</AlertDialogTitle>
            <AlertDialogDescription>
              La app dejará de leer sus correos. Los correos ya procesados no se borran.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mailbox-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="mailbox-delete-confirm">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default MailboxesPanel;
